import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  Button,
  ComboBox
} from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import {
  BriefcaseBusinessIcon,
  CalendarDaysIcon,
  CalendarRangeIcon,
  CircleXIcon,
  FileWarningIcon,
  GanttChartSquareIcon,
  LayersIcon,
  LayoutTemplateIcon,
  ShapesIcon
} from '@ttab/elephant-ui/icons'
import type { Block } from '@ttab/elephant-api/newsdoc'
import { View, ViewHeader } from '@/components/View'
import { Form } from '@/components/Form'
import { UserMessage } from '@/components/UserMessage'
import { ValidationCue } from '@/components/ValidationCue'
import { Prompt } from '@/components/Prompt'
import { useDocumentDefaults, useIsOnline, useRegistry, useSections } from '@/hooks'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useActiveAuthor } from '@/hooks/useActiveAuthor'
import { useStories } from '@/hooks/useStories'
import { useEditorialInfoTypes } from '@/hooks/useEditorialInfoType'
import { convertToISOStringInTimeZone } from '@/shared/datetime'
import { fetch as fetchPlannings } from '@/lib/index/fetch-plannings-twirp'
import type { DefaultValueOption } from '@/types'
import type { QueryParams } from '@/hooks/useQuery'
import {
  MEMO_NEWSVALUE,
  MEMO_SECTION_TITLE,
  memoCandidateColumn,
  memoCandidateColumnOrder,
  memoTemplates
} from '@/shared/templates/memos'
import type { MemoCandidateColumn } from '@/shared/templates/memos'
import { renderMemoLines } from '@/shared/templates/memos/lib/renderMemo'
import {
  resolveMemoAssignmentTitle,
  resolveMemoHeading,
  resolveMemoTitle
} from '@/shared/templates/memos/lib/resolveMemoTitle'
import {
  resolveMemoAssignmentSlugline,
  resolveMemoSlugline
} from '@/shared/templates/memos/lib/resolveMemoSlugline'
import { initialNewsvalueFilter } from '@/shared/templates/memos/lib/newsvalueFilter'
import { initialSectionFilter } from '@/shared/templates/memos/lib/sectionFilter'
import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'
import { isBlankBlock } from '@/shared/templates/memos/lib/insertedDocument'
import type {
  InsertedMemo, InsertedMemoDocument
} from '@/shared/templates/memos/lib/insertedDocument'
import { useMemoCandidates } from './hooks/useMemoCandidates'
import type { MemoDayRange } from './hooks/useMemoCandidates'
import { useMemoDateRange } from './hooks/useMemoDateRange'
import { useColumnDateRanges } from './hooks/useColumnDateRanges'
import type { SeededSpan } from './hooks/useSeededSpan'
import { useInsertableMemoDocuments } from './hooks/useInsertableMemoDocuments'
import { useDefaultedField } from './hooks/useDefaultedField'
import { useConfirmCreate } from './hooks/useConfirmCreate'
import { additionsFor } from './lib/additions'
import { distinctDocuments, holdsDocument, withoutDocument } from './lib/blockCopies'
import { createMemo } from './lib/createMemo'
import { isMemoComplete, memoValidity } from './lib/memoValidity'
import type { MemoValidity } from './lib/memoValidity'
import { fileSection } from './lib/fileSection'
import { sharedSection } from './lib/sharedSection'
import { groupByNewsvalue } from './lib/groupByNewsvalue'
import { resolveMemoDay } from './lib/memoDay'
import { insertBySection } from './lib/sectionOrder'
import { insertTarget } from './lib/insertTarget'
import { moveItemIntoBlock, moveItemNear } from './lib/moveSelected'
import type { MoveContext } from './lib/moveSelected'
import { MemoPreview } from './components/MemoPreview'
import { CandidateColumn } from './components/CandidateColumn'
import type { Insertable } from './components/InsertableDocuments'
import { MemoComboBox } from './components/MemoComboBox'
import { MemoDateFields } from './components/MemoDateFields'
import { MemoTextField } from './components/MemoTextField'
import { OfflineBanner } from './components/OfflineBanner'

type PlanningOption = Omit<DefaultValueOption, 'payload'> & {
  payload: { slugline?: string, section?: string, startDate?: string }
}

/**
 * The candidate filters, stamped with the template they were seeded for. What
 * to filter by follows from the template, and deriving it during render rather
 * than resetting it in an effect keeps the first render after a template change
 * from querying with the previous template's filters.
 */
interface MemoFilters {
  templateId: string
  newsvalues: Record<MemoCandidateColumn, string[]>
  sections: Record<MemoCandidateColumn, string[]>
  stories: Record<MemoCandidateColumn, string>
}

/**
 * The same filter value for every candidate column, for what is not a
 * per-column concept - the section a memo as a whole is written for - and for
 * the empty filter a dialog opens with.
 */
function everyColumn(values: string[]): Record<MemoCandidateColumn, string[]> {
  return { print: values, planning: values, article: values, events: values }
}

/** The same single value for every candidate column, the way `everyColumn` is. */
function everyColumnValue(value: string): Record<MemoCandidateColumn, string> {
  return { print: value, planning: value, article: value, events: value }
}

/**
 * Split a list of memo items into the columns they belong to. Every source
 * type maps to exactly one column, so nothing is dropped and nothing is
 * counted twice - which is what lets each column's own button reach the whole
 * selection between them.
 */
function byColumn(items: MemoItem[]): Record<MemoCandidateColumn, MemoItem[]> {
  const grouped: Record<MemoCandidateColumn, MemoItem[]> = {
    print: [],
    planning: [],
    article: [],
    events: []
  }

  for (const item of items) {
    grouped[memoCandidateColumn(item.sourceType)].push(item)
  }

  return grouped
}

/** The set without `id`, or the same set where it had none. */
function withoutId(ids: Set<string>, id: string): Set<string> {
  if (!ids.has(id)) {
    return ids
  }

  const next = new Set(ids)

  next.delete(id)

  return next
}

/** The days each of the given columns draws its candidates from, off its own span. */
function columnDays(
  columns: MemoCandidateColumn[],
  spans: Record<MemoCandidateColumn, SeededSpan>
): Partial<Record<MemoCandidateColumn, MemoDayRange>> {
  const days: Partial<Record<MemoCandidateColumn, MemoDayRange>> = {}

  for (const column of columns) {
    days[column] = { from: spans[column].date, to: spans[column].lastDate }
  }

  return days
}

/** What one PM is headed, titled and sluglined with. */
interface PmFields {
  heading: string
  assignmentTitle: string
  assignmentSlugline: string
}

/** One PM a create makes, and the editorial-info type it is about. */
interface PmToCreate extends PmFields {
  type?: { id: string, title: string }
}

/**
 * The picked types after picking `id`: a template taking one type swaps it,
 * one taking several toggles it, up to its `max`.
 */
function withTypeToggled(ids: string[], id: string, max: number): string[] {
  if (max <= 1) {
    return [id]
  }

  if (ids.includes(id)) {
    return ids.filter((current) => current !== id)
  }

  if (ids.length >= max) {
    return ids
  }

  return [...ids, id]
}

/**
 * One create's worth of the dialog - a memo, or one per picked type: every
 * field, every default the template feeds them, and the create itself.
 *
 * Nothing here outlives the memos it is building, which is what lets
 * `MemoCreation` below hand the editor a fresh form by remounting this one.
 */
const MemoForm = ({ id, query, onClose, onRestart }: {
  id: string
  query?: QueryParams
  /** Close the dialog, with what to open once a PM is created. */
  onClose: (created?: MemoCreated) => void
  /**
   * Start the next memo without closing the dialog. Answered by a remount, so
   * every field here goes back to the default it opened on.
   */
  onRestart: () => void
}): JSX.Element => {
  const { repository, index, locale, timeZone } = useRegistry()
  const { data: session } = useSession()
  const { t } = useTranslation()
  const sections = useSections({ sort: 'title' })
  // Unsorted, and so the same array between renders, for seeding the filters
  // without giving them a new identity every render.
  const sectionObjects = useSections()
  const stories = useStories({ sort: 'title' })
  const editorialInfoTypes = useEditorialInfoTypes({ sort: 'title' })
  const activeAuthor = useActiveAuthor({ full: false })
  const defaults = useDocumentDefaults()
  const isOnline = useIsOnline()
  const { hasPrint } = useFeatureFlags(['hasPrint'])

  /**
   * The templates this deployment writes memos from. A Print-PM is about what
   * is going into the paper, so it is offered where the print desk is and
   * nowhere else, and the template picker is the only way into one.
   */
  const templates = useMemo(
    () => memoTemplates.filter((candidate) => candidate.id !== 'print-pm' || hasPrint),
    [hasPrint]
  )

  const [templateId, setTemplateId] = useState('')
  const [sectionUuid, setSectionUuid] = useState('')
  const [sectionTouched, setSectionTouched] = useState(false)
  // In the order they were picked. A template with `editorialInfoTypeMax`
  // above one creates a PM per type; every other takes one at most.
  const [editorialInfoTypeIds, setEditorialInfoTypeIds] = useState<string[]>([])
  // The planning the first of several PMs was filed in, which a create retried
  // after a later one failed files the rest in too.
  const [createdPlanningId, setCreatedPlanningId] = useState<string | undefined>()
  // Set once a PM is saved under the form's `id`. Unlike `createdPlanningId`,
  // a template switch leaves it be: saves have no version check, so reusing
  // the id would overwrite that PM.
  const formIdSavedRef = useRef(false)
  const [selectedPlanning, setSelectedPlanning] = useState<PlanningOption | undefined>()
  const previousSectionPlanningIdRef = useRef(selectedPlanning?.value)

  const [filterState, setFilterState] = useState<MemoFilters>(() => ({
    templateId: '',
    newsvalues: everyColumn([]),
    sections: everyColumn([]),
    stories: everyColumnValue('')
  }))

  const [selected, setSelected] = useState<MemoItem[]>([])
  // Selected items whose block hasn't been chosen yet - only meaningful once a
  // template has more than one active block, since a single-block template has
  // nothing to ask about. These stay out of the preview until resolved.
  const [unassignedIds, setUnassignedIds] = useState<Set<string>>(new Set())
  // The PMs already written today that the editor folded into this one, in the
  // order they were picked, each in the block it was put in.
  const [inserted, setInserted] = useState<InsertedMemo[]>([])
  const [removedBlockIds, setRemovedBlockIds] = useState<Set<string>>(() => new Set())

  const template = useMemo(
    () => templates.find((candidate) => candidate.id === templateId),
    [templates, templateId]
  )

  const sectionOrder = template?.sectionOrder ?? []

  const filters = useMemo((): MemoFilters => {
    if (filterState.templateId === templateId) {
      return filterState
    }

    return {
      templateId,
      newsvalues: template ? initialNewsvalueFilter(template) : everyColumn([]),
      // Recomputed as the sections load, until the editor's first toggle stores it.
      sections: template ? initialSectionFilter(template, sectionObjects) : everyColumn([]),
      // A story is never seeded from the template - a memo is written about a
      // day's material, and narrowing it to one story is the editor's choice.
      stories: everyColumnValue('')
    }
  }, [filterState, templateId, template, sectionObjects])

  const today = useMemo(
    () => convertToISOStringInTimeZone(new Date(), timeZone).slice(0, 10),
    [timeZone]
  )

  // The day the memo itself belongs to - see the helper for what that is and
  // for why a planning's bare date is taken as written rather than parsed.
  const memoDate = useMemo(
    () => resolveMemoDay({
      planningStartDate: selectedPlanning?.payload.startDate,
      queryFrom: query?.from,
      today,
      timeZone
    }),
    [selectedPlanning, query?.from, today, timeZone]
  )

  // The days the candidate query asks for, and separately the days the memo is
  // about - see the hook for why those are not the same two dates, and for how
  // the span's ends are seeded and when they stop following the template.
  const memoSpan = useMemoDateRange({
    template,
    templateId,
    memoDate,
    planningId: selectedPlanning?.value
  })

  const { date, lastDate, titleDate, titleLastDate, shiftToWeekday } = memoSpan

  // A template whose columns are about different days asks each column for its
  // own instead, and the span above is then only what the titles follow.
  const columnDates = useColumnDateRanges({
    template,
    templateId,
    memoDate,
    planningId: selectedPlanning?.value
  })

  const variants = useMemo(() => template?.variants ?? [], [template])

  /**
   * The shape the memo is written in: the variant whose weekday the first day
   * the memo is about falls on. A day matching none of them - a weekend PM
   * whose range the editor has moved onto a Tuesday - leaves the first variant,
   * since a memo has to be written as one of its shapes.
   *
   * Read off the days rather than picked and remembered, which is what keeps
   * the shape and the days from ever disagreeing: picking a variant moves the
   * range onto that variant's weekday (see the picker below), and moving the
   * range picks the variant of the weekday it lands on. Whichever of the two
   * the editor touched last decides, and nothing the memo is titled, slugged or
   * opened with can name days the candidate query is not asking for.
   *
   * The day the memo is about rather than the span's first, for the same reason
   * the titles read that one - which for a template with variants is the same
   * day either way, `titleFollowsSpan` being what lets a variant be selected at
   * all.
   */
  const variant = useMemo(() => {
    if (!variants.length) {
      return undefined
    }

    const weekday = new Date(`${titleDate}T00:00:00`).getDay()

    return variants.find((entry) => entry.startWeekday === weekday) ?? variants[0]
  }, [variants, titleDate])

  const sectionChoice = template?.sectionChoice ?? 'fixed'

  // A planning the memo is filed into says which section it belongs to, so it
  // fills the picker in - until the editor picks one themselves, and picking a
  // different planning is a fresh start for that, same as it is for the date.
  // Filling the picker rather than replacing it is what keeps the editor from
  // being stuck when a planning carries a section the datastore has no record
  // of, which is a section they can neither see nor change.
  //
  // A `suggest` picker derives its section during render instead, so all it
  // takes from here is the fresh start.
  useEffect(() => {
    const planningChanged = previousSectionPlanningIdRef.current !== selectedPlanning?.value
    previousSectionPlanningIdRef.current = selectedPlanning?.value

    if (sectionChoice === 'fixed') {
      return
    }

    if (planningChanged) {
      setSectionTouched(false)
    }

    if (sectionChoice === 'suggest' || (!planningChanged && sectionTouched)) {
      return
    }

    const planningSection = selectedPlanning?.payload.section

    if (planningSection) {
      setSectionUuid(planningSection)
    }
  }, [selectedPlanning, sectionChoice, sectionTouched])

  // The section most memos are filed in, or the one a template pins itself to,
  // named by title since section uuids differ between environments. A title
  // that matches nothing blocks creation instead of failing at the backend.
  const pinnedSectionTitle = template?.filingSection
  const fixedSectionTitle = pinnedSectionTitle ?? MEMO_SECTION_TITLE

  const fixedSection = useMemo(() => {
    const name = fixedSectionTitle.toLocaleLowerCase()
    const match = sections.find((section) => section.title.trim().toLocaleLowerCase() === name)

    if (!match) {
      return undefined
    }

    return { uuid: match.id, title: match.title }
  }, [sections, fixedSectionTitle])

  const chosenSection = useMemo(() => {
    const match = sections.find((section) => section.id === sectionUuid)

    if (!match) {
      return undefined
    }

    return { uuid: match.id, title: match.title }
  }, [sections, sectionUuid])

  // The picked types the datastore knows of, in the order they were picked.
  const chosenEditorialInfoTypes = useMemo(() => editorialInfoTypeIds.flatMap((typeId) => {
    const match = editorialInfoTypes.find((type) => type.id === typeId)

    return match ? [{ id: match.id, title: match.title }] : []
  }), [editorialInfoTypes, editorialInfoTypeIds])

  // Several types make a PM each, so the fields can only show one of them:
  // the first, read-only - see `pmsToCreate` for what each PM gets.
  const multipleTypes = chosenEditorialInfoTypes.length > 1

  const chosenEditorialInfoTypeTitle = chosenEditorialInfoTypes.at(0)?.title

  // Attaching to an existing planning means filing the memo where that
  // planning already lives, so its section decides where the memo goes - over
  // the template's own fixed section and over whatever the columns were
  // narrowed to alike. Where it is filed is all it decides; see
  // `writtenForSection` below. A template that asks the editor for a section is
  // answered by the picker instead, which the planning only pre-fills - see the
  // effect above.
  const selectedPlanningSection = useMemo(() => {
    if (!selectedPlanning) {
      return undefined
    }

    const match = sections.find((section) => section.id === selectedPlanning.payload.section)

    if (!match) {
      return undefined
    }

    return { uuid: match.id, title: match.title }
  }, [sections, selectedPlanning])

  const selectedPostsSection = useMemo(
    () => sharedSection(selected, sections),
    [selected, sections]
  )

  // What a `suggest` picker shows: the editor's own pick, else the attached
  // planning's section, else the one the picked posts share, else the desk's.
  const suggestedSection = sectionTouched
    ? chosenSection
    : selectedPlanningSection ?? selectedPostsSection ?? fixedSection

  // Which columns to show follows the template's sources rather than what came
  // back, so an empty events column still says there are no events that day.
  // Declared up here because the section the memo is written for is read off
  // the filters of exactly these columns.
  const columns = useMemo(
    () => memoCandidateColumnOrder.filter((column) => (
      template?.sources.some((source) => memoCandidateColumn(source.type) === column)
    )),
    [template]
  )

  // The one section the candidates have been narrowed to, if it is one - what
  // makes a framtids-PM Utrikes' rather than the desk's. Narrowing to two is a
  // memo about the desk that skips a few sections, the same as narrowing to
  // none, and neither is a memo written for a section.
  //
  // Every column has to say the same one: a memo whose events are Utrikes'
  // while its plannings are still the whole desk's is a memo about the desk,
  // however its calendar was narrowed, and must not go out titled Utrikes'.
  const filterSection = useMemo(() => {
    const picked = new Set(columns.flatMap((column) => filters.sections[column]))
    const narrowed = !!columns.length && columns.every(
      (column) => filters.sections[column].length === 1
    )

    if (!narrowed || picked.size !== 1) {
      return undefined
    }

    const match = sections.find((section) => section.id === [...picked][0])

    if (!match) {
      return undefined
    }

    return { uuid: match.id, title: match.title }
  }, [columns, filters, sections])

  // The section the memo is written for, which is what its titles and sluglines
  // name - not the same question as where it is filed, since a memo written
  // across the desk is filed somewhere all the same. A template that asks for a
  // section is answered by its picker; everything else is written for one only
  // once the editor has narrowed the candidates to it.
  //
  // An attached planning deliberately says nothing here. Filing a PM into a
  // section's planning is not writing it about that section: the columns go on
  // listing every section's material, and titling the memo "TT:s utrikes vecko-pm"
  // over a list that is half Inrikes' - which is what the render would then also
  // strip the section off, see `withoutWrittenSection` - would be naming a
  // section the memo is not about.
  const writtenForSection = sectionChoice === 'select' ? chosenSection : filterSection

  // Where it is filed: the picked section, the one the template pins itself to,
  // or else the planning it is going into, the section it is written for, or
  // the desk's own for a memo that is written for none.
  const memoSection = fileSection({
    sectionChoice,
    pinned: !!pinnedSectionTitle,
    chosenSection,
    suggestedSection,
    selectedPlanningSection,
    writtenForSection,
    fixedSection
  })

  // Only a template that asks the editor for a section narrows the candidates
  // by it on the memo's behalf, the picker being the search scope there and the
  // generic filter saying the same thing twice - for every column alike, since
  // the memo's section isn't a per-column concept. Every other template leaves
  // the section filter to the editor, who narrows each column separately, and
  // an attached planning cannot take that away from them.
  const needsSection = sectionChoice === 'select'

  const filterSections: Record<MemoCandidateColumn, string[]> = needsSection
    ? everyColumn(memoSection ? [memoSection.uuid] : [])
    : filters.sections

  // Every span the candidates are drawn from runs forwards - see `validRange`.
  const validRange = memoSpan.validRange
    && (!columnDates || columns.every((column) => columnDates[column].validRange))

  // Nothing to ask the index for until the dialog knows what to ask about -
  // a template to draw sources from, and for a memo written for one section,
  // that section too.
  const canQuery = !!template && validRange && (!needsSection || !!memoSection)

  const { items, isLoading, error } = useMemoCandidates(template, {
    from: date,
    to: lastDate,
    ranges: columnDates ? columnDays(columns, columnDates) : undefined,
    timeZone,
    newsvalues: filters.newsvalues,
    sections: filterSections,
    stories: filters.stories
  }, canQuery)

  // The day's already written PMs, offered only by the templates that say they
  // take one - see `insertableDocuments` on the template schema.
  const { documents: insertableDocuments } = useInsertableMemoDocuments(
    today,
    timeZone,
    template?.insertableDocumentsFilter,
    !!template?.insertableDocuments
  )

  // What a newly created planning is called. Not what the PM is headed with -
  // that is `suggestedFields` below, which feeds the preview's own field.
  const defaultTitle = useMemo(() => {
    if (!template) {
      return ''
    }

    return resolveMemoTitle(template, {
      date: new Date(`${titleDate}T00:00:00`),
      toDate: new Date(`${titleLastDate}T00:00:00`),
      sectionTitle: writtenForSection?.title,
      variant
    })
  }, [template, titleDate, titleLastDate, writtenForSection, variant])

  // What a PM about the type is headed, titled and sluglined with.
  //
  // The heading is the planning's title for most templates and something of
  // the template's own where it declares one. The type comes along for the
  // latter: a print PM is headed with what it is about - "PM BNL torsdagen 3
  // september" - and so holds nothing until a type has been picked. The
  // assignment slugline names the type where the template has one -
  // "print-pm-bnl" rather than "print-pm" - or follows a pattern of its own,
  // like the vecko-PM's week.
  const suggestedFields = useCallback((typeTitle: string | undefined): PmFields => {
    if (!template) {
      return { heading: '', assignmentTitle: '', assignmentSlugline: '' }
    }

    const options = {
      editorialInfoTypeTitle: typeTitle,
      date: new Date(`${titleDate}T00:00:00`),
      toDate: new Date(`${titleLastDate}T00:00:00`),
      sectionTitle: writtenForSection?.title,
      variant
    }

    return {
      heading: resolveMemoHeading(template, options),
      assignmentTitle: resolveMemoAssignmentTitle(template, options),
      assignmentSlugline: resolveMemoAssignmentSlugline(template, options)
    }
  }, [template, titleDate, titleLastDate, writtenForSection, variant])

  // The fields open on the first picked type's - which, with several picked,
  // is all they show. See `pmsToCreate`.
  const suggested = useMemo(
    () => suggestedFields(chosenEditorialInfoTypeTitle),
    [suggestedFields, chosenEditorialInfoTypeTitle]
  )

  // The template's own slugline, which is what its planning item is tagged
  // with. A chosen editorial-info type is deliberately not part of it: the type
  // names this memo, while the planning item is the day's print PM whichever
  // type it is about, and more than one of them is filed against it.
  const defaultPlanningSlugline = useMemo(() => {
    if (!template) {
      return ''
    }

    return resolveMemoSlugline(template, {
      date: new Date(`${titleDate}T00:00:00`),
      toDate: new Date(`${titleLastDate}T00:00:00`),
      sectionTitle: writtenForSection?.title,
      variant
    })
  }, [template, titleDate, titleLastDate, writtenForSection, variant])

  // The fields the template fills in until the editor takes one over, each
  // built on the default resolved for it above.
  const planningTitle = useDefaultedField(defaultTitle)
  const assignmentTitle = useDefaultedField(suggested.assignmentTitle)
  const planningSlugline = useDefaultedField(defaultPlanningSlugline)
  const assignmentSlugline = useDefaultedField(suggested.assignmentSlugline)
  const memoHeading = useDefaultedField(suggested.heading)

  // The fields that follow the planning title - see `planningTitleMirrors` on the
  // template schema.
  const mirrorFields = { assignmentTitle, memoHeading }
  const mirrorNames = template?.planningTitleMirrors ?? []
  const planningTitleMirrors = mirrorNames.map((field) => mirrorFields[field])

  // A picked planning hides its title field, so a mirrored assignment title
  // takes over as what the other mirrors follow.
  const assignmentTitleMirrors = selectedPlanning && mirrorNames.includes('assignmentTitle')
    ? mirrorNames.filter((field) => field !== 'assignmentTitle').map((field) => mirrorFields[field])
    : []

  // What a single PM is headed with: the preview's own field, and nothing else.
  // With several types picked, each PM is headed with its own type's
  // suggestion instead - see `pmsToCreate`.
  //
  // The planning, the assignment and the PM start out named the same thing,
  // which is what the template suggests to all three, but the headline a PM
  // goes out under is not the same concern as what the work is filed as - the
  // desk renames one without meaning to rename the others. So the heading is a
  // field of its own rather than a second view of the assignment title, and is
  // written where it is read.
  const memoTitle = memoHeading.value

  // Handing these five fields back to the template is a dependency of the
  // effect below, and a field is a new object every render - so the reset
  // callbacks are taken out here, where they are the stable references the hook
  // made them. Depending on a whole field would run that effect on every render
  // and never let any of them be edited at all.
  const { reset: resetPlanningTitle } = planningTitle
  const { reset: resetPlanningSlugline } = planningSlugline
  const { reset: resetAssignmentSlugline } = assignmentSlugline
  const { reset: resetAssignmentTitle } = assignmentTitle
  const { reset: resetMemoHeading } = memoHeading

  // Changing template or section is a fresh start: what the memo is being
  // written about has changed under the selection, and without the section it
  // would keep rendering the items of the section it was pointed at before.
  // Which blocks the editor removed belongs to the same fresh start - a
  // different template has a different set of blocks entirely.
  //
  // Moving the span is deliberately not part of it, and neither is attaching a
  // planning. A picked item is a copy of what it renders as rather than a live
  // reference to a candidate, so it goes on being what the memo says whichever
  // days the columns are listing - which is the whole point of the span:
  // picking a few headlines from one day and a few from the next is one memo,
  // and reaching the span out to find the second day cannot be what throws the
  // first day's away. A planning says where the memo is filed and which day it
  // belongs to, neither of which changes what the columns list, so it has no
  // more claim on the selection than the span does.
  //
  // The pending block assignments go with it: they name items that are no
  // longer in the memo, and an id left behind is a batch bar counting an item
  // nothing can resolve - which is a create button that never creates again.
  //
  // Only a section the candidates are drawn from counts: a `suggest` picker
  // says where the memo is filed, which is no reason to throw the picks away.
  const candidateSectionUuid = needsSection ? sectionUuid : ''

  useEffect(() => {
    setSelected([])
    setRemovedBlockIds(new Set())
    setUnassignedIds(new Set())
  }, [templateId, candidateSectionUuid])

  // The chosen types, a hand-picked section and every hand-edited text field -
  // both titles, both sluglines and the memo's own heading - belong to the
  // template that prompted them: switching templates hands each back to what
  // the new one suggests rather than carrying the previous one's over. An
  // inserted PM belongs to the same reset: not every template takes one, and
  // the one that does not would carry it invisibly. So does the planning a
  // part-failed create filed PMs in, which holds the previous template's PMs.
  //
  // The date range is deliberately not part of this: an inserted PM is picked
  // by hand out of the day's own, not found by the candidate query, so moving
  // the days the memo covers says nothing about it.
  useEffect(() => {
    setEditorialInfoTypeIds([])
    setCreatedPlanningId(undefined)
    setSectionTouched(false)
    resetPlanningTitle()
    resetAssignmentTitle()
    resetAssignmentSlugline()
    resetPlanningSlugline()
    resetMemoHeading()
    setInserted([])
  }, [
    templateId,
    resetPlanningTitle,
    resetAssignmentTitle,
    resetAssignmentSlugline,
    resetPlanningSlugline,
    resetMemoHeading
  ])

  // Several types are a PM each, headed and titled for its own type, so a
  // hand-edit made for the one type has nothing left to apply to.
  useEffect(() => {
    if (!multipleTypes) {
      return
    }

    resetAssignmentTitle()
    resetAssignmentSlugline()
    resetMemoHeading()
  }, [multipleTypes, resetAssignmentTitle, resetAssignmentSlugline, resetMemoHeading])

  // An item still waiting on the batch bar's block choice is not in the memo
  // yet - it stays in its candidate column until it is.
  const assignedSelected = useMemo(
    () => selected.filter((item) => !unassignedIds.has(item.id)),
    [selected, unassignedIds]
  )

  // The preview is headed with the same string the created memo will be - the
  // heading field sits in the preview itself, so what is typed there is the
  // headline the PM goes out under, see `memoTitle` above. With several types
  // picked, that is only the first PM's heading.
  const preview = useMemo(
    () => (template
      ? renderMemoLines(template, assignedSelected, memoTitle, {
        variant,
        inserted,
        sectionTitle: writtenForSection?.title
      })
      : []),
    [template, assignedSelected, memoTitle, variant, inserted, writtenForSection]
  )

  // Alphabetical, so a template is found by its name however many there are.
  const templateOptions = useMemo(
    () => templates
      .map((option) => ({ value: option.id, label: option.label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'sv')),
    [templates]
  )

  const sectionOptions = useMemo(
    () => sections.map((section) => ({ value: section.id, label: section.title })),
    [sections]
  )

  const storyOptions = useMemo(
    () => stories.map((story) => ({ value: story.id, label: story.title })),
    [stories]
  )

  const editorialInfoTypeOptions = useMemo(
    () => editorialInfoTypes.map((type) => ({ value: type.id, label: type.title })),
    [editorialInfoTypes]
  )

  const variantOptions = useMemo(
    () => variants.map((entry) => ({ value: entry.id, label: entry.label })),
    [variants]
  )

  // What the memo has taken, by document rather than by item: a template that
  // puts one document in every block holds an item per block, and a candidate
  // row is checked while the memo holds any of them.
  const selectedIds = useMemo(
    () => new Set(selected.map((item) => item.documentId)),
    [selected]
  )

  // The candidates and the memo, each split into the columns they belong to -
  // what every column's own list, and its own button, is drawn from. Counted by
  // document for the same reason: a column's button asks how much of the column
  // is in the memo, and a document taken into two blocks was taken once.
  const itemsByColumn = useMemo(() => byColumn(items), [items])
  const selectedByColumn = useMemo(
    () => byColumn(distinctDocuments(selected)),
    [selected]
  )

  const columnLabels: Record<MemoCandidateColumn, string> = {
    print: t('memo:labels.print'),
    planning: t('memo:labels.planning'),
    article: t('memo:labels.article'),
    events: t('memo:labels.events')
  }

  // Whether a picked candidate goes in all the blocks at once rather than
  // waiting on the batch bar for the one it belongs in - see
  // `insertsIntoEveryBlock` on the template schema.
  const insertsIntoEveryBlock = !!template?.insertsIntoEveryBlock

  // Whether the memo is about one post, a new pick replacing the old one - see
  // `singleItem` on the template schema.
  const singleItem = !!template?.singleItem

  /** The items one picked candidate adds to the memo - see `additionsFor`. */
  const additionsOf = (item: MemoItem): MemoItem[] => (
    additionsFor(item, activeBlocks, insertsIntoEveryBlock)
  )

  const forgetUnassigned = (ids: string[]) => {
    setUnassignedIds((current) => {
      if (!ids.some((id) => current.has(id))) {
        return current
      }

      const next = new Set(current)

      ids.forEach((id) => next.delete(id))

      return next
    })
  }

  /**
   * Take a candidate into the memo, or out of it again.
   *
   * Out takes every copy of it with it: the row is one document, and a document
   * half in the memo is not what unchecking it says. Taking one of its copies
   * out of one block is the preview's cross, not this.
   *
   * In a one-post memo a new pick takes the place of the old one.
   */
  const toggle = (item: MemoItem) => {
    const picked = selected.filter((entry) => entry.documentId === item.documentId)

    if (picked.length) {
      setSelected((current) => withoutDocument(current, item.documentId))
      forgetUnassigned(picked.map((entry) => entry.id))

      return
    }

    const additions = additionsOf(item)

    if (singleItem) {
      forgetUnassigned(selected.map((entry) => entry.id))
      setSelected(additions)
    } else {
      setSelected((current) => insertBySection(current, additions, sectionOrder))
    }

    if (insertsIntoEveryBlock || activeBlocks.length <= 1) {
      return
    }

    setUnassignedIds((current) => new Set(current).add(item.id))
  }

  /** Take one copy of an item out of the one block it sits in. */
  const removeSelected = (item: MemoItem) => {
    setSelected((current) => current.filter((entry) => entry.id !== item.id))
    forgetUnassigned([item.id])
  }

  /**
   * Fold an already written PM into one of this memo's blocks.
   *
   * The document is kept as it was read, rather than by id: what the preview
   * shows and what gets saved is then the PM as it stood when it was picked,
   * and a later edit to it cannot change a memo the editor has already looked
   * over. Which block it goes in is the editor's answer to the list's own
   * question - the night's five belong under the block the memo heads them
   * with, whose opening line is then the memo's. A block the template pins the
   * PM to is put back first if the editor had removed it.
   */
  const insertDocument = (insertable: InsertedMemoDocument, blockId: string) => {
    setRemovedBlockIds((current) => withoutId(current, blockId))
    setInserted((current) => [...current, { ...insertable, blockId }])
  }

  const removeInserted = (documentId: string) => {
    setInserted((current) => current.filter((entry) => entry.id !== documentId))
  }

  /**
   * Take one line of an inserted PM out, keeping the rest - four of the night's
   * five without retyping any of them.
   *
   * The line is named by the source block it was rendered from, which is the
   * very object the document carries: `renderMemoLines` passes it through
   * untouched, so no id has to exist or be trusted. A PM left with no lines at
   * all - its blank ones do not count - is no longer inserted, and its title in
   * the list says so again.
   */
  const removeInsertedLine = (documentId: string, block: Block | undefined) => {
    if (!block) {
      return
    }

    setInserted((current) => current.flatMap((entry) => {
      if (entry.id !== documentId) {
        return [entry]
      }

      const blocks = entry.blocks.filter((candidate) => candidate !== block)

      if (blocks.every(isBlankBlock)) {
        return []
      }

      return [{ ...entry, blocks }]
    }))
  }

  /**
   * Take everything the column is listing that the memo has not already got,
   * in the order the column lists it - by newsvalue, the order its rows are in
   * - so the memo reads the way the column the editor was looking at did.
   */
  const selectAll = (column: MemoCandidateColumn) => {
    const picked = new Set(selected.map((entry) => entry.documentId))
    const additions = groupByNewsvalue(itemsByColumn[column])
      .flatMap((group) => group.items)
      .filter((item) => !picked.has(item.documentId))

    setSelected((current) => insertBySection(
      current,
      additions.flatMap(additionsOf),
      sectionOrder
    ))

    if (insertsIntoEveryBlock || activeBlocks.length <= 1 || !additions.length) {
      return
    }

    setUnassignedIds((current) => {
      const next = new Set(current)

      additions.forEach((item) => next.add(item.id))

      return next
    })
  }

  /**
   * Drop the column's picks, listed or not. An item picked before the filter
   * or the span moved is still the column's, and with no memo-wide clear left
   * this button is the only thing that can reach it.
   */
  const deselectAll = (column: MemoCandidateColumn) => {
    const removedIds = selected
      .filter((entry) => memoCandidateColumn(entry.sourceType) === column)
      .map((entry) => entry.id)

    setSelected((current) => current.filter(
      (entry) => memoCandidateColumn(entry.sourceType) !== column
    ))

    forgetUnassigned(removedIds)
  }

  // The blocks the editor has not removed, in template order - the only ones
  // an item can live in or move into.
  const activeBlocks = useMemo(
    () => template?.blocks.filter((block) => !removedBlockIds.has(block.id)) ?? [],
    [template, removedBlockIds]
  )

  const moveContext = useMemo(
    (): MoveContext => ({ blocks: template?.blocks ?? [] }),
    [template]
  )

  const dropNear = (item: MemoItem, neighborId: string, position: 'before' | 'after') => {
    setSelected((current) => moveItemNear(current, item, neighborId, position, moveContext))
  }

  const dropIntoBlock = (item: MemoItem, blockId: string) => {
    setSelected((current) => moveItemIntoBlock(current, item, blockId))
  }

  /**
   * Answer the question the batch bar asks: put everything still unassigned
   * into one block, all at once. Items already assigned - from an earlier
   * batch, or by drag - are untouched, since only the ids still in
   * `unassignedIds` are rewritten, and slotted into the block by section since
   * this is where they first show up in the preview.
   */
  const assignUnassigned = (blockId: string) => {
    setSelected((current) => insertBySection(
      current.filter((entry) => !unassignedIds.has(entry.id)),
      current
        .filter((entry) => unassignedIds.has(entry.id))
        .map((entry) => ({ ...entry, blockId })),
      sectionOrder
    ))
    setUnassignedIds(new Set())
  }

  // Removing a block merges its items - and the PMs inserted into it - into the
  // next active block, or the preceding one when it is the last, there being no
  // "next" there. A memo always keeps at least one block, so the last active one
  // cannot be removed: there would be nowhere for its items to go, and the
  // preview would be left with nothing to arrange. The block itself comes back
  // empty if reinserted; nothing tracks where its content went once merged.
  //
  // An item the target block already holds a copy of is dropped rather than
  // merged - the two are the same document, and a block listing it twice says
  // the same thing twice. The copy that was already there is the one kept, so
  // the keyword it was given in the block that survives survives with it.
  const removeBlock = (blockId: string) => {
    if (activeBlocks.length < 2) {
      return
    }

    const index = activeBlocks.findIndex((block) => block.id === blockId)

    if (index < 0) {
      return
    }

    const target = activeBlocks[index + 1] ?? activeBlocks[index - 1]

    setSelected((current) => current.flatMap((entry) => {
      if (entry.blockId !== blockId) {
        return [entry]
      }

      if (holdsDocument(current, target.id, entry)) {
        return []
      }

      return [{ ...entry, blockId: target.id }]
    }))
    setInserted((current) => current.map((entry) => (
      entry.blockId === blockId ? { ...entry, blockId: target.id } : entry
    )))
    setRemovedBlockIds((current) => new Set(current).add(blockId))
  }

  // A block collapse can leave a single active block behind while items are
  // still unassigned - there is no longer a choice to make, so they resolve
  // into it the same way a single-block template resolves them on selection.
  useEffect(() => {
    if (activeBlocks.length > 1 || unassignedIds.size === 0) {
      return
    }

    const soleBlock = activeBlocks[0]

    if (!soleBlock) {
      return
    }

    setSelected((current) => current.map((entry) => (
      unassignedIds.has(entry.id) ? { ...entry, blockId: soleBlock.id } : entry
    )))
    setUnassignedIds(new Set())
  }, [activeBlocks, unassignedIds])

  const insertBlock = (blockId: string) => {
    setRemovedBlockIds((current) => withoutId(current, blockId))
  }

  // The keyword lives on the selected copy of the item, the way its block does,
  // so it survives reordering and hopping between blocks. An item that is
  // deselected and picked again starts over without one.
  const setKeyword = (item: MemoItem, keyword: string | undefined) => {
    setSelected((current) => current.map((entry) => (
      entry.id === item.id ? { ...entry, keyword } : entry
    )))
  }

  const toggleFilter = (column: MemoCandidateColumn, key: 'newsvalues' | 'sections', value: string) => {
    const current = filters[key][column]

    setFilterState({
      ...filters,
      [key]: {
        ...filters[key],
        [column]: current.includes(value)
          ? current.filter((entry) => entry !== value)
          : [...current, value]
      }
    })
  }

  /**
   * Narrow a column to one story, or widen it back out. Only one story at a
   * time: a memo written about two of them is a memo about the day's material,
   * which is what no filter at all already gives.
   */
  const selectStory = (column: MemoCandidateColumn, uuid: string) => {
    setFilterState({
      ...filters,
      stories: {
        ...filters.stories,
        [column]: filters.stories[column] === uuid ? '' : uuid
      }
    })
  }

  // One PM per picked type, or the one PM for a template that takes none. A
  // single PM is the fields' own, hand-edits and all; several get their own
  // type's suggestions each.
  const pmsToCreate: PmToCreate[] = multipleTypes
    ? chosenEditorialInfoTypes.map((type) => ({ type, ...suggestedFields(type.title) }))
    : [{
        type: chosenEditorialInfoTypes.at(0),
        heading: memoTitle,
        assignmentTitle: assignmentTitle.value,
        assignmentSlugline: assignmentSlugline.value
      }]

  // A planning an earlier, part-failed create filed PMs in stands in for one
  // the editor attached.
  const planningId = selectedPlanning?.value ?? createdPlanningId

  const confirmCreate = useConfirmCreate()

  // What the memo still lacks, field by field. Unlike the environment below it
  // does not disable the buttons: a click with something missing marks what is
  // missing instead, the way the planning dialog does.
  const validity = memoValidity({
    hasTemplate: !!template,
    // A template that does not require items is written without - see `requiresItems`.
    requiresItems: !!template?.requiresItems,
    contentCount: selected.length + inserted.length,
    unassignedCount: unassignedIds.size,
    hasPlanning: !!planningId,
    planningTitle: planningTitle.value,
    planningSlugline: planningSlugline.value,
    pms: pmsToCreate,
    hasSection: !!memoSection,
    validRange,
    requiresEditorialInfoType: !!template?.requiresEditorialInfoType,
    editorialInfoTypeCount: chosenEditorialInfoTypes.length
  })

  const isComplete = isMemoComplete(validity)

  // A memo is two documents written one after the other - its own, and the
  // assignment hung off a planning - so a create begun without a connection
  // has a half-finished PM to leave behind.
  const environmentIsSane = !!session?.accessToken && isOnline

  // Set by the first create attempted with something missing, and kept: a
  // field marked missing stays marked until it is filled in.
  const [showValidation, setShowValidation] = useState(false)

  const cue = (check: keyof MemoValidity): boolean => showValidation && !validity[check]

  /**
   * Create the memo - or, with several types picked, a memo per type once the
   * editor has confirmed it - and say what to do once they exist.
   *
   * Several are created one after the other, the first on the attached or a
   * new planning and the rest on that same one. One that fails stops the rest:
   * the types already created leave the picker, so creating again picks up
   * where it stopped, on the same planning. `whenCreated` gets the first PM
   * this create made, and that planning, which holds every one of them.
   *
   * Every value is read into `createMemo`'s arguments from this render, so
   * nothing `whenCreated` does can reach the documents being written. That is
   * what lets the create-and-restart button put the form back to its defaults
   * the moment the create resolves.
   */
  const create = async (
    whenCreated: (created: { memoId: string, planningId: string }) => void
  ): Promise<void> => {
    if (!template || !isComplete || !memoSection) {
      setShowValidation(true)

      // Rejecting is what gives the button back - see the catch below.
      throw new Error('Memo is incomplete')
    }

    // The buttons are disabled then, so this is a connection lost mid-click.
    if (!environmentIsSane) {
      throw new Error('Memo cannot be created offline or signed out')
    }

    if (pmsToCreate.length > 1) {
      const confirmed = await confirmCreate.ask(pmsToCreate.map((pm) => pm.heading.trim()))

      // Rejecting is what gives the button back - see the catch below.
      if (!confirmed) {
        throw new Error('Memo creation cancelled')
      }
    }

    let createdCount = 0
    let firstMemoId: string | undefined
    const createdTypeIds: string[] = []
    let filedIn = planningId

    try {
      for (const [position, pm] of pmsToCreate.entries()) {
        // The form's id is the first PM's, unless a part-failed create already
        // saved one under it.
        const documentId = position === 0 && !formIdSavedRef.current
          ? id
          : crypto.randomUUID()

        filedIn = await createMemo({
          repository,
          session,
          id: documentId,
          template,
          items: selected,
          planningId: filedIn,
          planningTitle: filedIn ? undefined : planningTitle.value.trim(),
          assignmentTitle: pm.assignmentTitle.trim(),
          // What the memo is headed with - the preview's heading field for a
          // single PM, the type's suggestion for one of several. Sent whether
          // or not a planning is created, unlike `planningTitle` - a planning
          // already there takes no title, but the memo is still headed with one.
          memoTitle: pm.heading.trim(),
          section: memoSection,
          newsvalue: MEMO_NEWSVALUE,
          language: defaults.language,
          localDate: memoDate,
          isoDateTime: `${memoDate}T${new Date().toISOString().split('T')[1].split('.')[0]}Z`,
          author: activeAuthor ? { id: activeAuthor.id, name: activeAuthor.name } : undefined,
          slugline: pm.assignmentSlugline.trim(),
          planningSlugline: filedIn ? undefined : planningSlugline.value.trim(),
          editorialInfoType: pm.type ? { uuid: pm.type.id } : undefined,
          variant,
          inserted,
          writtenForSection: writtenForSection?.title
        })

        createdCount += 1
        firstMemoId ??= documentId

        if (documentId === id) {
          formIdSavedRef.current = true
        }

        if (pm.type) {
          createdTypeIds.push(pm.type.id)
        }
      }
    } catch (ex) {
      const message = ex instanceof Error ? ex.message : t('errors:messages.unknown')
      console.error('Failed to create memo:', ex)
      toast.error(t('errors:toasts.creationFailed', { error: message }))

      if (createdCount) {
        setEditorialInfoTypeIds((current) => current.filter(
          (typeId) => !createdTypeIds.includes(typeId)
        ))
        setCreatedPlanningId(filedIn)
      }

      // Handing the rejection on is what gives the button back. `Form.Submit`
      // disables it for the length of the submit and clears that from a
      // rejected promise and nothing else (`components/Form/Submit.tsx`), so
      // swallowing the error here left the editor looking at a failure toast
      // above a button they could no longer press.
      throw ex
    }

    // Always set by now: a create makes one PM at the least.
    if (filedIn && firstMemoId) {
      whenCreated({ memoId: firstMemoId, planningId: filedIn })
    }
  }

  // Closes onto the PM, or onto its planning for a template that says so.
  const handleCreate = () => create((created) => {
    onClose(template?.opensPlanning
      ? { view: 'Planning', id: created.planningId }
      : { view: 'Editor', id: created.memoId })
  })

  /**
   * Create the memo and stay in the dialog, on a form back at its defaults -
   * how several PMs get written in a row.
   *
   * The toast is what says the memo exists, the dialog it was built in still
   * being open. It names no particular memo, and cannot: the form is remounted
   * onto the next memo's defaults the moment the create resolves, so there is
   * no title left to quote by the time anyone reads it.
   */
  const handleCreateAndRestart = () => {
    return create(() => {
      toast.success(t('memo:toasts.created'))
      onRestart()
    })
  }

  const fetchPlanningOptions = useCallback(
    (query: string) => fetchPlannings(query, session, t, index, locale, timeZone),
    [session, t, index, locale, timeZone]
  )

  // Where the titles follow one another, letting go of a planning hands them all
  // back to the template rather than keeping what was typed while it was picked.
  const clearPlanning = () => {
    setSelectedPlanning(undefined)

    if (planningTitleMirrors.length > 0) {
      resetPlanningTitle()
      planningTitleMirrors.forEach((field) => field.reset())
    }
  }

  const insertedById = useMemo(
    () => new Map(inserted.map((document) => [document.id, document])),
    [inserted]
  )

  const insertedIds = useMemo(
    () => new Set(inserted.map((document) => document.id)),
    [inserted]
  )

  // Only a template that says it takes an already written PM is offered the
  // day's, and only when there is one to offer.
  const target = insertTarget(template, activeBlocks)
  const insertable: Insertable | undefined = target && insertableDocuments.length
    ? {
        documents: insertableDocuments,
        insertedIds,
        target,
        onInsert: insertDocument,
        onRemove: removeInserted
      }
    : undefined

  // Removed blocks, in template order, for the "add it back" strip below the
  // preview.
  const removedBlocks = useMemo(
    () => template?.blocks.filter((block) => removedBlockIds.has(block.id)) ?? [],
    [template, removedBlockIds]
  )

  return (
    <View.Root asDialog>
      <ViewHeader.Root asDialog>
        <ViewHeader.Title
          name='Plannings'
          title={t('memo:actions.new')}
          icon={FileWarningIcon}
          iconColor='#F2CC8F'
          asDialog
        />
        <ViewHeader.Content />
        <ViewHeader.Action asDialog onDialogClose={() => onClose()} />
      </ViewHeader.Root>

      {/*
        Outside `View.Content` on purpose - that is the only place the banner can
        pin itself from. Below the header rather than above it, so the pill
        floats over the form and not over the dialog's own title and close.
      */}
      {!isOnline && <OfflineBanner />}

      <View.Content>
        <Form.Root asDialog>
          <Form.Content>
            {/*
              What the memo itself is, and below it what it is built from.
              Neither half is headed on screen - a ComboBox trigger shows only
              its selection once something is picked - so the `aria-label` each
              section carries is what tells them apart to a screen reader, and
              the eye goes by what the two hold.
            */}
            <section
              aria-label={t('memo:labels.memo')}
              className='px-4 flex flex-col gap-4 w-full'
            >
              {/*
                The template drives every other default below it - titles, filters
                and the candidate list - so it comes first, and is what the dialog
                opens focused on. Focusing the trigger has to happen here rather
                than on the section, which takes no focus of its own.

                A `suggest` template puts where the memo is filed beside it,
                shown up front so the section is a choice rather than a default
                nobody saw.
              */}
              <Form.Group icon={LayoutTemplateIcon}>
                <div className='flex flex-wrap items-center gap-2 w-full'>
                  <ValidationCue show={cue('template')}>
                    <MemoComboBox
                      autoFocus
                      fitToViewport
                      max={1}
                      options={templateOptions}
                      selectedOptions={templateOptions.filter((option) => option.value === templateId)}
                      placeholder={t('memo:placeholders.template')}
                      onSelect={(option) => setTemplateId(option.value)}
                    />
                  </ValidationCue>

                  {sectionChoice === 'suggest' && (
                    <ValidationCue show={cue('section')}>
                      <MemoComboBox
                        max={1}
                        sortOrder='label'
                        options={sectionOptions}
                        selectedOptions={sectionOptions.filter(
                          (option) => option.value === suggestedSection?.uuid
                        )}
                        placeholder={t('memo:placeholders.memoSection')}
                        onSelect={(option) => {
                          setSectionTouched(true)
                          setSectionUuid(option.value)
                        }}
                      />
                    </ValidationCue>
                  )}
                </div>
              </Form.Group>

              {/*
                Picking an existing planning files the memo into it instead of
                spawning a new one - the same choice Timeless/Wire creation
                offer. Its own title makes the new-planning title redundant.
              */}
              <Form.Group icon={GanttChartSquareIcon}>
                <ComboBox
                  max={1}
                  size='xs'
                  modal={true}
                  className='min-w-0 truncate max-w-48'
                  selectedOptions={selectedPlanning ? [selectedPlanning] : []}
                  placeholder={t('planning:move.pickPlanning')}
                  fetch={fetchPlanningOptions}
                  minSearchChars={2}
                  fetchDebounce={300}
                  onSelect={(option) => {
                    if (option.value === selectedPlanning?.value) {
                      clearPlanning()
                      return
                    }

                    setSelectedPlanning({
                      value: option.value,
                      label: option.label,
                      payload: option.payload as PlanningOption['payload']
                    })
                  }}
                  translationStrings={{
                    nothingFound: t('common:misc.nothingFound'),
                    searching: t('common:misc.searching')
                  }}
                />

                {!!selectedPlanning && (
                  <Button
                    variant='ghost'
                    className='text-muted-foreground flex size-4 p-0 hover:bg-accent2'
                    onClick={(e) => {
                      e.preventDefault()
                      clearPlanning()
                    }}
                    render={<CircleXIcon size={18} strokeWidth={1.75} />}
                  />
                )}
              </Form.Group>

              {/*
                A new planning gets its own slugline beside its title,
                pre-filled with the template's suggestion the way the
                assignment's is - the backend writes this one onto the planning
                item and never onto the assignment, so editing one leaves the
                other as it was.

                Both wrap back onto separate lines in a dialog too narrow to
                hold them side by side, which is the layout they used to have.
              */}
              {!selectedPlanning && (
                <Form.Group icon={CalendarDaysIcon}>
                  <div className='flex flex-wrap items-center gap-2 w-full'>
                    <ValidationCue show={cue('planningTitle')} className='max-w-full'>
                      <MemoTextField
                        label={t('memo:labels.planningTitle')}
                        placeholder={t('memo:labels.planningTitle')}
                        value={planningTitle.value}
                        onValueChange={(value) => {
                          planningTitle.set(value)
                          planningTitleMirrors.forEach((field) => field.set(value))
                        }}
                      />
                    </ValidationCue>

                    <ValidationCue show={cue('planningSlugline')} className='max-w-full'>
                      <MemoTextField
                        label={t('memo:labels.planningSlugline')}
                        placeholder={t('memo:labels.planningSlugline')}
                        value={planningSlugline.value}
                        onValueChange={planningSlugline.set}
                      />
                    </ValidationCue>
                  </div>
                </Form.Group>
              )}

              {/*
                Every memo needs an assignment slugline - pre-filled with the
                template's (or, for Print-PM, the chosen type's) suggested
                default, hand-editable and always required. With several types
                picked, each PM gets its own type's, so the fields only show the
                first one's, read-only.
              */}
              <Form.Group icon={BriefcaseBusinessIcon}>
                <div className='flex flex-wrap items-center gap-2 w-full'>
                  <ValidationCue show={cue('assignmentTitle')} className='max-w-full'>
                    <MemoTextField
                      label={t('memo:labels.assignmentTitle')}
                      placeholder={t('memo:labels.assignmentTitle')}
                      value={assignmentTitle.value}
                      onValueChange={(value) => {
                        assignmentTitle.set(value)
                        assignmentTitleMirrors.forEach((field) => field.set(value))
                      }}
                      readOnly={multipleTypes}
                    />
                  </ValidationCue>

                  <ValidationCue show={cue('assignmentSlugline')} className='max-w-full'>
                    <MemoTextField
                      label={t('memo:labels.assignmentSlugline')}
                      placeholder={t('memo:labels.assignmentSlugline')}
                      value={assignmentSlugline.value}
                      onValueChange={assignmentSlugline.set}
                      readOnly={multipleTypes}
                    />
                  </ValidationCue>
                </div>
              </Form.Group>

              {/*
                A memo written per section is filed where the editor says, and
                that section is what its candidates are drawn from, so it belongs
                with the memo rather than with the filters. A planning fills it
                in, but never takes it away: hiding the picker behind an
                attached planning left the section neither shown nor settable.
              */}
              {sectionChoice === 'select' && (
                <Form.Group icon={ShapesIcon}>
                  <ValidationCue show={cue('section')}>
                    <MemoComboBox
                      max={1}
                      sortOrder='label'
                      options={sectionOptions}
                      selectedOptions={sectionOptions.filter((option) => option.value === sectionUuid)}
                      placeholder={t('memo:placeholders.section')}
                      onSelect={(option) => {
                        setSectionTouched(true)
                        setSectionUuid(option.value)
                      }}
                    />
                  </ValidationCue>
                </Form.Group>
              )}

              {/*
                Only a template funneled into a publish pipe by its editorial-info
                type needs one picked before the memo can be created - every other
                template is unaffected. One with `editorialInfoTypeMax` above one
                takes several, and creates a PM per type.
              */}
              {template?.requiresEditorialInfoType && (
                <Form.Group icon={LayersIcon}>
                  <ValidationCue show={cue('editorialInfoType')}>
                    <MemoComboBox
                      max={template.editorialInfoTypeMax}
                      sortOrder='label'
                      options={editorialInfoTypeOptions}
                      selectedOptions={editorialInfoTypeOptions.filter(
                        (option) => editorialInfoTypeIds.includes(option.value)
                      )}
                      placeholder={t('memo:placeholders.editorialInfoType')}
                      onSelect={(option) => setEditorialInfoTypeIds((current) => withTypeToggled(
                        current,
                        option.value,
                        template.editorialInfoTypeMax
                      ))}
                    />
                  </ValidationCue>
                </Form.Group>
              )}

              {/*
                A template written in more than one shape asks which - answered
                by the days the memo covers, and picking one here moves those
                days onto it: a helg-PM written as the Saturday's is about the
                Saturday through the Monday, so those are the days its
                candidates are drawn from and the days it is titled after.
              */}
              {!!variantOptions.length && (
                <Form.Group icon={CalendarRangeIcon}>
                  <MemoComboBox
                    max={1}
                    sortOrder='label'
                    options={variantOptions}
                    selectedOptions={variantOptions.filter(
                      (option) => option.value === variant?.id
                    )}
                    placeholder={t('memo:placeholders.variant')}
                    onSelect={(option) => {
                      const picked = variants.find((entry) => entry.id === option.value)

                      if (!picked) {
                        return
                      }

                      shiftToWeekday(picked.startWeekday)
                    }}
                  />
                </Form.Group>
              )}

              {/*
                The newsvalue is fixed for every memo, and so is the section for
                every template that covers the whole desk - either the one every
                memo is filed in or the attached planning's - so an environment
                where it resolves to nothing has to say so, since there is no
                picker to say it with. A create is refused either way.
                Sections start out empty while the datastore loads, which is
                indistinguishable from none of them matching. A `suggest` picker
                that opens empty for the same reason says so too.
              */}
              {sectionChoice !== 'select' && !!sections.length && !memoSection && (
                <p className='text-sm text-destructive'>
                  {t('memo:errors.sectionMissing', { section: fixedSectionTitle })}
                </p>
              )}

              <UserMessage asDialog>
                {selectedPlanning
                  ? t('wires:creation.withPlanningHint')
                  : t('wires:creation.noPlanningHint')}
                <p>{t('memo:hints.draftOnly')}</p>
              </UserMessage>
            </section>

            {/*
              The days the candidates are drawn from, which is why they sit with
              the filters: a memo is regularly built out of two of them, and the
              second is not always ahead - the morning PM reaches back for the
              night's late news. Both ends are offered whatever the template, and
              normally neither names the memo: what its titles are dated by is the
              day it belongs to, which is the day the editor is on and does not
              move with these - see `titleDate`. A template whose title names a
              span the editor picks out rather than one that follows from the day
              is titled by both ends - see `titleFollowsSpan`.

              A template whose columns are about different days puts a pair in
              each column instead - see `hasColumnDates`.
            */}
            {!columnDates && (
              <section
                aria-label={t('memo:labels.selection')}
                className='px-4 flex flex-col gap-4 w-full'
              >
                <Form.Group icon={CalendarDaysIcon}>
                  <MemoDateFields
                    span={memoSpan}
                    today={today}
                    invalid={showValidation && !memoSpan.validRange}
                  />
                </Form.Group>
              </section>
            )}

            {/*
              The candidates and what they become, beside each other once the
              dialog is wide enough to hold both. Picking an item and reading
              what it did to the memo is one motion, so the list and the preview
              belong on screen at the same time rather than stacked with the
              list's own scroll between them.

              The breakpoint is the dialog's width rather than the window's: the
              dialog is capped at min(94vw, 1800px) (see AddButtonGroup), so a
              viewport breakpoint says nothing about the room in here. Below it
              the two go back to being stacked, which is the only way they both
              fit.

              How much room that takes depends on how many candidate columns
              the template has - a row of three or four needs more of it before
              they can hold their own filters without wrapping than a row of
              one or two does, so the split waits for a wider dialog the more
              columns there are rather than squeezing all of them beside the
              preview at the same point.
            */}
            <div className='@container w-full px-4'>
              <div
                className={cn(
                  'grid grid-cols-1 gap-4',
                  columns.length <= 2 && '@4xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]',
                  columns.length === 3 && '@6xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]',
                  columns.length >= 4 && '@7xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]'
                )}
              >
                <div className='flex flex-col gap-4 min-w-0'>
                  <section aria-label={t('memo:labels.candidates')} className='flex flex-col gap-2'>
                    {!!error && <p className='text-sm text-destructive'>{error.message}</p>}

                    {/*
                      No template picked is not the same as a search that came back
                      empty, so it gets its own message rather than falling through
                      to "nothing found" below - which is what the fixed section and
                      newsvalue this renders for could otherwise be mistaken for
                      having found nothing.
                    */}
                    {!template && (
                      <p className='text-sm text-muted-foreground'>{t('memo:errors.templateRequired')}</p>
                    )}

                    {/*
                      Nothing was fetched yet when the section is still missing, which
                      is not the same thing as there being nothing to find - so the
                      one is never reported as the other. What settles the section is
                      the picker, whether the editor filled it in themselves or an
                      attached planning did it for them.
                    */}
                    {sectionChoice === 'select' && !memoSection && (
                      <p className='text-sm text-muted-foreground'>{t('memo:errors.sectionRequired')}</p>
                    )}

                    {canQuery && !isLoading && !error && !items.length && !selected.length && (
                      <p className='text-sm text-muted-foreground'>{t('common:misc.nothingFound')}</p>
                    )}

                    {/*
                      Offline the columns are whatever the last fetch left
                      behind, and nothing picked from them can be created, so
                      they are put out of reach until the connection is back.
                      The preview is not: a memo already put together can go on
                      being rearranged while it waits.
                    */}
                    {/*
                      Every column in one row once the dialog is wide enough
                      to give each of them room for their own filters without
                      wrapping - never the odd one out on a row of its own
                      while the others stay put, and never all of them
                      shoulder to shoulder so tight a filter button wraps
                      inside its column instead. Below that width they stack
                      full-width instead, one column at a time, which is what
                      the plain single-column default below already is.
                    */}
                    <div
                      className={cn(
                        'grid gap-4',
                        columns.length === 2 && '@3xl:grid-cols-2',
                        columns.length === 3 && '@5xl:grid-cols-3',
                        columns.length === 4 && '@7xl:grid-cols-4',
                        !isOnline && 'opacity-50 pointer-events-none'
                      )}
                    >
                      {columns.map((column) => (
                        <CandidateColumn
                          key={column}
                          column={column}
                          label={columnLabels[column]}
                          items={itemsByColumn[column]}
                          isLoading={isLoading}
                          selectedIds={selectedIds}
                          unassignedIds={unassignedIds}
                          selectedCount={selectedByColumn[column].length}
                          onToggle={toggle}
                          onSelectAll={selectAll}
                          onDeselectAll={deselectAll}
                          filter={{
                            newsvalues: filters.newsvalues[column],
                            sections: filters.sections[column],
                            story: filters.stories[column]
                          }}
                          sectionOptions={sectionOptions}
                          storyOptions={storyOptions}
                          showSectionFilter={!needsSection}
                          showSelectAll={!singleItem}
                          onToggleFilter={toggleFilter}
                          onSelectStory={selectStory}
                          dates={columnDates && {
                            span: columnDates[column],
                            today,
                            invalid: showValidation && !columnDates[column].validRange
                          }}
                        />
                      ))}
                    </div>
                  </section>
                </div>

                <MemoPreview
                  lines={preview}
                  template={template}
                  activeBlocks={activeBlocks}
                  removedBlocks={removedBlocks}
                  selected={assignedSelected}
                  unassignedCount={unassignedIds.size}
                  insertedById={insertedById}
                  insertable={insertable}
                  onDropNear={dropNear}
                  onDropIntoBlock={dropIntoBlock}
                  onRemoveItem={removeSelected}
                  onSetKeyword={setKeyword}
                  onRemoveBlock={removeBlock}
                  onInsertBlock={insertBlock}
                  onAssignUnassigned={assignUnassigned}
                  onRemoveInserted={removeInserted}
                  onRemoveInsertedLine={removeInsertedLine}
                  editableHeading={{
                    value: memoHeading.value,
                    onChange: memoHeading.set,
                    readOnly: multipleTypes
                  }}
                  invalid={{
                    heading: cue('heading'),
                    unassigned: cue('unassigned'),
                    items: cue('items')
                  }}
                  // Not while picks wait on a block: those are posts chosen.
                  itemsRequired={!validity.items && unassignedIds.size === 0}
                />
              </div>
            </div>
          </Form.Content>

          <Form.Footer className='flex justify-between flex-row-reverse'>
            <Form.Submit
              onSubmit={handleCreate}
              onSecondarySubmit={handleCreateAndRestart}
              disableOnSubmit
            >
              <div className='flex gap-2'>
                {/*
                  `role` is how Form.Submit knows which of its handlers a
                  button answers to, rather than an ARIA role - it complains at
                  a `type='button'` child without one.
                */}
                <Button
                  type='button'
                  role='secondary'
                  variant='secondary'
                  disabled={!environmentIsSane}
                >
                  {t('memo:actions.newAndClear')}
                </Button>

                <Button type='submit' disabled={!environmentIsSane}>
                  {t('common:actions.create')}
                </Button>
              </div>
            </Form.Submit>
          </Form.Footer>
        </Form.Root>
      </View.Content>

      {!!confirmCreate.pending && (
        <Prompt
          title={t('memo:prompts.createSeveral')}
          primaryLabel={t('common:actions.create')}
          cancelLabel={t('common:actions.abort')}
          onPrimary={() => confirmCreate.answer(true)}
          onCancel={() => confirmCreate.answer(false)}
        >
          <ul className='flex flex-col gap-1 text-sm'>
            {confirmCreate.pending.map((heading, position) => (
              <li key={`${position}-${heading}`}>{heading}</li>
            ))}
          </ul>
        </Prompt>
      )}
    </View.Root>
  )
}

/** What the dialog opens once it closes on a create: the PM, or its planning. */
export interface MemoCreated {
  view: 'Editor' | 'Planning'
  id: string
}

/**
 * Dialog view for creating an auto-generated editorial-info memo (PM).
 *
 * The editor picks a template and the days, takes what the memo is built from out
 * of the candidate columns the template fills - nothing is picked for them -
 * and what survives their reordering is rendered into an editorial-info
 * assignment - one per picked type where the template takes several - on a new
 * planning or on one the editor attaches. The memo is only ever created as a
 * draft, never published.
 *
 * The dialog can be kept open across several memos, so it owns the one thing
 * two of them cannot share: the id the form's first memo is saved under. A
 * second memo saved under the first one's id would overwrite it, so each form
 * gets an id of its own, and every PM past the first in a several-type create
 * gets a fresh one in `create`. Since that id is also the form's key, a fresh
 * id is a fresh form. That is the whole of "rensa": every default is seeded on mount,
 * including the ones nothing here could reach otherwise, such as the date
 * range's own record of having been edited by hand.
 */
export const MemoCreation = ({ id, query, onClose }: {
  id: string
  query?: QueryParams
  onClose: (created?: MemoCreated) => void
}): JSX.Element => {
  const [memoId, setMemoId] = useState(id)

  return (
    <MemoForm
      key={memoId}
      id={memoId}
      query={query}
      onClose={onClose}
      onRestart={() => setMemoId(crypto.randomUUID())}
    />
  )
}
